// The ender dragon fight, in the world (DIM-3). PURE (ENG-1).
//
// M21.1 built the boss itself — the flight-phase cycle, the crystal heal, the
// killable gate. What was missing was the *arena*: the crystals that shield it were
// only ever indexed when a player PLACED one, so the ones the End generator stands
// on the pillars did not count, and nothing spawned the dragon or let you leave
// afterwards. This module supplies those three pure pieces:
//
//   - `generatedCrystalCells` — where worldgen put the crystals, from the seed
//     alone, so the host can seed its shield index without scanning chunks.
//   - `exitPortalCells` / `buildExitPortal` — the bedrock fountain with its
//     end-portal eye and the dragon egg on top, raised when the dragon dies.
//   - `dragonSpawnPoint` / `endFightPending` — where the boss enters, and whether
//     this End still owes you a fight (read off the world, so it survives a
//     reload with no extra saved state).
import { END } from '../config';
import { endPillars } from '../gen/end/terrain';

/** A block accessor the fight code reads/writes (a live World satisfies it). */
export interface EndFightAccess {
  get(x: number, y: number, z: number): number;
  set(x: number, y: number, z: number, state: number): void;
}

export type Cell = readonly [number, number, number];

/** The end-crystal cells the generator stands on the pillar ring, from the seed. */
export function generatedCrystalCells(seed: number | string | bigint): Cell[] {
  return endPillars(seed).map((p) => [p.x, p.topY + 1, p.z] as const);
}

/** Where the dragon enters: high over the middle of the central island. */
export function dragonSpawnPoint(): Cell {
  return [0.5, END.ISLAND_TOP + END.DRAGON_SPAWN_HEIGHT, 0.5];
}

/**
 * The exit portal's cells, relative to the island top: a sunken bedrock rim, a
 * 3×3 end-portal eye inside it, and a short bedrock plinth holding the dragon egg.
 * Returned as a plan so the host can both build it and test for it.
 */
export interface ExitPortalPlan {
  bedrock: Cell[];
  portal: Cell[];
  /** Where the dragon egg is laid, on top of the plinth. */
  egg: Cell;
  /** The cell a "has this End already been beaten?" probe should read. */
  probe: Cell;
}

export function exitPortalPlan(baseY: number = END.ISLAND_TOP): ExitPortalPlan {
  const bedrock: Cell[] = [];
  const portal: Cell[] = [];
  const r = END.EXIT_PORTAL_RADIUS;
  for (let dz = -r; dz <= r; dz++) {
    for (let dx = -r; dx <= r; dx++) {
      const inner = Math.abs(dx) <= 1 && Math.abs(dz) <= 1;
      if (inner) portal.push([dx, baseY, dz]);
      else bedrock.push([dx, baseY, dz]);
      // A raised rim one course up, leaving the portal face open.
      if (Math.abs(dx) === r || Math.abs(dz) === r) bedrock.push([dx, baseY + 1, dz]);
    }
  }
  // The central plinth rises out of the portal's middle and carries the egg.
  for (let i = 1; i <= END.EXIT_PLINTH_HEIGHT; i++) bedrock.push([0, baseY + i, 0]);
  return {
    bedrock,
    portal,
    egg: [0, baseY + END.EXIT_PLINTH_HEIGHT + 1, 0],
    probe: [1, baseY, 1], // a portal cell, never a bedrock one
  };
}

/** Raise the exit portal (and lay the egg) — what a slain dragon leaves behind. */
export function buildExitPortal(
  access: EndFightAccess,
  ids: { bedrock: number; endPortal: number; dragonEgg?: number },
  baseY: number = END.ISLAND_TOP,
): ExitPortalPlan {
  const plan = exitPortalPlan(baseY);
  for (const [x, y, z] of plan.bedrock) access.set(x, y, z, ids.bedrock);
  for (const [x, y, z] of plan.portal) access.set(x, y, z, ids.endPortal);
  if (ids.dragonEgg !== undefined) {
    const [ex, ey, ez] = plan.egg;
    access.set(ex, ey, ez, ids.dragonEgg);
  }
  return plan;
}

/**
 * Does this End still owe the player a dragon fight? True until the exit portal
 * exists — which is state the world itself carries, so a reloaded (or re-entered)
 * End does not respawn a boss you already beat, with no extra save data.
 */
export function endFightPending(
  access: EndFightAccess,
  endPortalState: number,
  baseY: number = END.ISLAND_TOP,
): boolean {
  const [px, py, pz] = exitPortalPlan(baseY).probe;
  return access.get(px, py, pz) !== endPortalState;
}
